import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';

import { ReviewRating } from '../../components';

import css from './Reviews.module.css';

const initialsFromName = name =>
  (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

const ReviewAvatar = ({ author }) => {
  const displayName = author?.displayName || '';
  if (author?.profileImageUrl) {
    return (
      <img
        className={css.avatarImage}
        src={author.profileImageUrl}
        alt={displayName}
      />
    );
  }
  return <span className={css.avatarInitials}>{initialsFromName(displayName)}</span>;
};

const Review = props => {
  const { review, intl } = props;

  const dateString = intl.formatDate(review.createdAt, { month: 'long', year: 'numeric' });
  const displayName = review.author?.displayName || '';

  return (
    <div className={css.review}>
      <div className={css.avatar}>
        <ReviewAvatar author={review.author} />
      </div>
      <div>
        <ReviewRating
          rating={review.rating}
          className={css.mobileReviewRating}
          reviewStarClassName={css.reviewRatingStar}
        />
        <p className={css.reviewContent}>{review.content}</p>
        <p className={css.reviewInfo}>
          {displayName}
          <span className={css.separator}>•</span>
          {dateString}
          <span className={css.desktopSeparator}>•</span>
          <span className={css.desktopReviewRatingWrapper}>
            <ReviewRating
              rating={review.rating}
              className={css.desktopReviewRating}
              reviewStarClassName={css.reviewRatingStar}
            />
          </span>
        </p>
      </div>
    </div>
  );
};

/**
 * A component that renders a list of reviews.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Array} props.reviews - The reviews to render (flat DTOs from reviewQueryService)
 * @returns {JSX.Element}
 */
const Reviews = props => {
  const intl = useIntl();
  const { className, rootClassName, reviews = [] } = props;
  const classes = classNames(rootClassName || css.root, className);

  return (
    <ul className={classes}>
      {reviews.map(r => {
        return (
          <li key={`Review_${r.id}`} className={css.reviewItem}>
            <Review review={r} intl={intl} />
          </li>
        );
      })}
    </ul>
  );
};

export default Reviews;
